import { useEffect, useMemo, useRef, useState } from "react";
import { searchPrices, getPublishedListBySlug, type PriceItem } from "../utils/api";
import "../styles/PublicList.css";
import logo from "../assets/Logo.png";

const DEFAULT_SLUG =
  (import.meta.env.VITE_PUBLIC_LIST_SLUG as string | undefined)?.trim() || "lista-actual";

function useDebounced<T>(value: T, delay = 350) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

export default function PublicList() {
  const [slug] = useState<string>(DEFAULT_SLUG);
  const [listName, setListName] = useState<string>("");
  const [q, setQ] = useState("");
  const dq = useDebounced(q, 350);

  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [rows, setRows] = useState<PriceItem[]>([]);
  const [total, setTotal] = useState(0);

  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const progRef = useRef<number>(0);

  useEffect(() => {
    let timer: number | null = null;
    if (loading) {
      progRef.current = 10;
      setProgress(10);
      timer = window.setInterval(() => {
        progRef.current = Math.min(90, progRef.current + 7);
        setProgress(progRef.current);
      }, 120);
    }
    return () => {
      if (timer) window.clearInterval(timer);
    };
  }, [loading]);

  useEffect(() => {
    (async () => {
      try {
        const list = await getPublishedListBySlug(slug);
        setListName(list?.name || "");
      } catch {
        setListName("");
      }
    })();
  }, [slug]);

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const res = await searchPrices({ slug, q: dq, page, pageSize });
        if (cancel) return;
        setRows(res.items);
        setTotal(res.total);
        progRef.current = 100;
        setProgress(100);
      } catch {
        if (!cancel) {
          setRows([]);
          setTotal(0);
        }
      } finally {
        if (!cancel) setTimeout(() => setLoading(false), 180);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [slug, dq, page, pageSize]);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / pageSize)), [total, pageSize]);
  const onPrev = () => setPage((p) => Math.max(1, p - 1));
  const onNext = () => setPage((p) => Math.min(totalPages, p + 1));

  const onShare = async (row: PriceItem) => {
    const text = `${row.product || ""} — PVP: ${fmtMoney(row.pvp)} ${
      row.no_tax ? `(Sin imp.: ${fmtMoney(row.no_tax)})` : ""
    }`;
    try {
      if (navigator.share) {
        await navigator.share({ title: row.product || "Producto", text });
      } else {
        await navigator.clipboard.writeText(text);
        alert("Copiado para compartir ✅");
      }
    } catch {}
  };

  return (
    <div className="pl-wrap">
      <header className="pl-header">
        <img className="pl-logo" src={logo} alt="Logo" />
        <div className="pl-titles">
          <h1>Lista de precios</h1>
          <p className="pl-sub">Buscá por código, producto, laboratorio o rubro</p>
        </div>
      </header>

      <div className="pl-search">
        <span className="pl-search-icon" aria-hidden="true">
          <svg viewBox="0 0 20 20" fill="none" stroke="currentColor">
            <path
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M19 19l-4-4m1-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </span>
        <input
          autoFocus
          value={q}
          onChange={(e) => {
            setPage(1);
            setQ(e.target.value);
          }}
          placeholder="Buscar producto, código, laboratorio o rubro…"
        />
      </div>

      <div className="pl-table">
        <div className="pl-table-head">
          <div>Presentación</div>
          <div>Precio</div>
          <div>Sin impuestos</div>
        </div>

        {rows.map((r) => (
          <div key={r.id} className="pl-row">
            <div className="pl-col-present">
              <div className="pl-prod">{r.product || "-"}</div>
              <div className="pl-muted">
                {r.lab ? `Lab: ${r.lab}` : null}
                {r.rubro ? ` · Rubro: ${r.rubro}` : null}
                {r.codebar ? ` · Código: ${r.codebar}` : null}
              </div>

              <details className="pl-more">
                <summary>Ver más &gt;</summary>
                <div className="pl-more-body">
                  <p><b>Laboratorio:</b> {r.lab || "-"}</p>
                  <p><b>Rubro:</b> {r.rubro || "-"}</p>
                  <p><b>Código:</b> {r.codebar || "-"}</p>
                  <p><b>Actualizado:</b> {fmtDate(r.created_at)}</p>
                </div>
              </details>
            </div>

            <div className="pl-col-money" data-label="Precios">
              {fmtMoney(r.pvp)}
            </div>
            <div className="pl-col-money" data-label="Sin impuestos">
              {fmtMoney(r.no_tax)}
            </div>
          </div>
        ))}

        {!loading && rows.length === 0 && (
          <div className="pl-empty">No se encontraron resultados.</div>
        )}
      </div>

      <div className="pl-pager">
        <button onClick={onPrev} disabled={page <= 1}>◀</button>
        <span>{page} / {totalPages}</span>
        <button onClick={onNext} disabled={page >= totalPages}>▶</button>
      </div>

      {loading && (
        <div className="pl-overlay">
          <div className="pl-loader">
            <div className="pl-bar">
              <div className="pl-bar-fill" style={{ width: `${progress}%` }} />
            </div>
            <div className="pl-perc">Buscando productos… {progress}%</div>
          </div>
        </div>
      )}
    </div>
  );
}

function fmtMoney(n?: number | null) {
  if (n === null || n === undefined) return "-";
  return new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency: "ARS",
    maximumFractionDigits: 2,
  }).format(Number(n));
}

function fmtDate(s?: string | null) {
  if (!s) return "-";
  const d = new Date(s);
  return d.toLocaleDateString("es-AR", { year: "numeric", month: "2-digit", day: "2-digit" });
}
